import axiosInstance from "../global/axiosInterceptor";
import {
  fetchAppearancesEndPoint,
  createAppearanceEndPoint,
  deleteAppearanceEndPoint,
  updateAppearanceEndPoint,
  submitAppearanceEndpoint,
  uploadAppearanceImageEndPoint,
  downloadAppearanceImageEndPoint,
  deleteAppearanceImageEndPoint,
  enrollAppearanceEndPoint,
} from "../configuration";

// Get all appearances (using mock data)
export const getAppearances = async (payload, activeSession) => {
  if (!activeSession?.accountId) {
    throw new Error("Active session with accountId is required");
  }
  try {
    const response = await axiosInstance.post(fetchAppearancesEndPoint(), {
      accountname: activeSession.accountId,
      filter: {
        $expand: "deploy",
      },
      ...payload
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching appearances:", error);
    throw error;
  }
};

// Delete appearance (using mock data)
export const deleteAppearance = async (appearanceId, activeSession) => {
  if (!appearanceId) {
    throw new Error("Appearance ID is required");
  }
  try {
    const response = await axiosInstance.delete(deleteAppearanceEndPoint(), {
          data: {
            accountname: activeSession.accountId,
            resourceidentifier: { id: appearanceId },
          },
        }
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting appearance:", error);
    throw error;
  }
};

// Create appearance (using mock data)
export const createAppearance = async (payload, activeSession) => {
  if (!payload) {
    throw new Error("Payload is required");
  }
  try {
    const response = await axiosInstance.post(createAppearanceEndPoint(), {
      accountname: activeSession.accountId,
      appearance: {
        name: payload.appearanceName
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error creating appearance:", error);
    throw error;
  }
};

// Update appearance with site IDs
export const updateAppearance = async (payload, activeSession) => {
  if (!payload.appearanceId) {
    throw new Error("Appearance ID is required");
  }
  try {
    const response = await axiosInstance.patch(updateAppearanceEndPoint(), {
      accountname: activeSession.accountId,
      resourceidentifier: {
        id: payload.appearanceId,
      },
      appearance: {
        siteids: payload.siteIds || [],
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error updating appearance:", error);
    throw error;
  }
};

// Submit appearance with companions
export const submitAppearance = async (payload, activeSession) => {
  if (!payload.appearanceId) {
    throw new Error("Appearance ID is required");
  }
  try {
    const response = await axiosInstance.post(submitAppearanceEndpoint(), {
      accountname: activeSession.accountId,
      resourceidentifier: {
        id: payload.appearanceId,
      },
      // companions: payload.companions || [],
    });
    return response.data;
  } catch (error) {
    console.error("Error submitting appearance:", error);
    throw error;
  }
};

// Download appearance image and create blob URL
export const downloadAppearanceImage = async (appearanceId, imageId, imageStatus, activeSession) => {
  if (!appearanceId || !imageId) {
    throw new Error("Appearance ID and image ID are required");
  }
  if (!activeSession?.accountId) {
    throw new Error("Active session with accountId is required");
  }
  try {
    const response = await axiosInstance.post(
      downloadAppearanceImageEndPoint(),
      {
        accountname: activeSession.accountId,
        resourceidentifier: {
          id: appearanceId,
        },
        imageid: imageId,
      },
      {
        responseType: 'blob',
        headers: {
          // Let server decide content-type; ensure we accept binary
          Accept: '*/*',
        },
      }
    );

    // Create blob URL from the binary data
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
    const objectUrl = URL.createObjectURL(blob);

    return {
      imageId,
      status: imageStatus,
      url: objectUrl,
      blob,
    };
  } catch (error) {
    console.error(`Error downloading image ${imageId}:`, error);
    throw error;
  }
};

// Download all images for an appearance
export const downloadAllAppearanceImages = async (appearanceId, images, activeSession) => {
  if (!images || images.length === 0) {
    return [];
  }

  try {
    // Download all images in parallel
    const downloadPromises = images.map(image => {
      const id = image?.imageId;
      const status = image?.status;
      const imageName = image?.imageName;

      if (!id) {
        console.warn(`Skipping image download - missing imageId:`, image);
        return Promise.resolve({ imageId: null, url: null, error: true, status: "", imageName });
      }
      return downloadAppearanceImage(appearanceId, id, status, activeSession)
        .then(result => ({ ...result, imageName }))
        .catch(error => {
          console.error(`Failed to download image ${id}:`, error);
          throw error;
        });
    });

  const downloadedImages = await Promise.all(downloadPromises);

    // Return array of image objects with blob URLs
    return downloadedImages.map(downloaded => ({
      id: downloaded.imageId,
      url: downloaded.url,
      blob: downloaded.blob,
      error: downloaded.error || false,
      status: downloaded.status,
      imageName: downloaded.imageName,
    }));
  } catch (error) {
    console.error("Error downloading appearance images:", error);
    throw error;
  }
};

// Delete a specific image for an appearance
export const deleteAppearanceImage = async (imageId, appearanceId, activeSession) => {
  if (!appearanceId || !imageId) {
    throw new Error("Appearance ID and image ID are required");
  }

  try {
    const response = await axiosInstance.post(
      deleteAppearanceImageEndPoint(),
      {
        accountname: activeSession.accountId,
        resourceidentifier: {
          id: appearanceId,
        },
        imageid: imageId,
      },
    );
    return response.data;
  } catch (error) {
    console.error(`Error deleting image ${imageId}:`, error);
    throw error;
  }
};

// Upload Appearance Image
export const uploadAppearanceImage = async (payload, activeSession) => {
  if (!payload.appearanceId) {
    throw new Error("Appearance id is required");
  };

  try {
    const formData = new FormData();
    formData.append("accountname", activeSession.accountId);
    formData.append("appearanceid", payload.appearanceId);
    formData.append("image", payload.file);

    const response = await axiosInstance.post(uploadAppearanceImageEndPoint(), formData);
    return response.data;
  } catch (error) {
    console.error("Error uploading appearance image:", error);
    throw error;
  }
};

// Enroll appearance to sites — add/remove run concurrently, partial failures reported individually
export const enrollAppearance = async (payload, activeSession) => {
  if (!payload.appearanceId) {
    throw new Error("Appearance ID is required");
  }

  const { sitesToAdd = [], sitesToRemove = [] } = payload;
  const calls = [];

  if (sitesToAdd.length > 0) {
    calls.push({
      type: "add",
      promise: axiosInstance.post(enrollAppearanceEndPoint(), {
        accountname: activeSession.accountId,
        resourceidentifier: { id: payload.appearanceId },
        siteIds: sitesToAdd,
        action: "add",
      }),
    });
  }

  if (sitesToRemove.length > 0) {
    calls.push({
      type: "remove",
      promise: axiosInstance.post(enrollAppearanceEndPoint(), {
        accountname: activeSession.accountId,
        resourceidentifier: { id: payload.appearanceId },
        siteIds: sitesToRemove,
        action: "remove",
      }),
    });
  }

  const settled = await Promise.allSettled(calls.map((c) => c.promise));
  const firstFailure = settled.find((r) => r.status === "rejected");
  if (firstFailure) {
    throw firstFailure.reason;
  }
  const firstFulfilled = settled.find((r) => r.status === "fulfilled");
  return firstFulfilled?.value?.data || null;
};
